import React, { useEffect, useState, useCallback } from "react";
import { Plus, Truck, Trash2, PackageCheck, Navigation, Clock, Zap, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import { SHIPMENT_STATUSES, COURIERS, shipmentClasses, formatINR } from "@/lib/ui";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-colors";
const selCls =
  "bg-[#0a0a0a] border border-white/10 text-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";

const EMPTY = {
  brand: "", customer_name: "", customer_email: "", items: "", courier: "Delhivery", tracking_number: "",
  status: "Pending Pickup", ship_date: "", expected_delivery: "", cod_amount: "", address: "",
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</span>
        <Icon size={17} className={accent} />
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}

export default function Shipping() {
  const [shipments, setShipments] = useState([]);
  const [clients, setClients] = useState([]);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [invoices, setInvoices] = useState([]);
  const [retOpen, setRetOpen] = useState(false);
  const [retShip, setRetShip] = useState(null);
  const [ret, setRet] = useState({ reason: "", refund_amount: "", invoice_id: "" });

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    api.get("/shipments", { params }).then((r) => setShipments(r.data));
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/invoices").then((r) => setInvoices(r.data));
  }, []);

  const simulate = async (id) => {
    const { data } = await api.post(`/shipments/${id}/simulate-courier`);
    toast.success(`Courier update → ${data.status}`);
    load();
  };

  const openReturn = (s) => {
    setRetShip(s);
    setRet({ reason: "", refund_amount: s.cod_amount ? String(s.cod_amount) : "", invoice_id: "" });
    setRetOpen(true);
  };

  const submitReturn = async () => {
    try {
      const { data } = await api.post(`/shipments/${retShip.id}/return`, {
        reason: ret.reason,
        refund_amount: Number(ret.refund_amount || 0),
        invoice_id: ret.invoice_id || null,
      });
      toast.success(data.refund_applied ? `RTO logged · ${formatINR(data.refund_applied)} refunded` : "Return / RTO logged");
      setRetOpen(false);
      load();
      api.get("/invoices").then((r) => setInvoices(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!form.brand || !form.customer_name) return toast.error("Brand and customer are required");
    try {
      await api.post("/shipments", { ...form, cod_amount: Number(form.cod_amount || 0) });
      toast.success("Shipment created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const updateStatus = async (id, newStatus) => {
    await api.post(`/shipments/${id}/status?status=${encodeURIComponent(newStatus)}`);
    toast.success(`Marked ${newStatus}`);
    load();
  };

  const remove = async (id) => { await api.delete(`/shipments/${id}`); load(); };

  const inTransit = shipments.filter((s) => ["In Transit", "Out for Delivery", "Picked Up"].includes(s.status)).length;
  const delivered = shipments.filter((s) => s.status === "Delivered").length;
  const pending = shipments.filter((s) => s.status === "Pending Pickup").length;

  return (
    <div>
      <PageHeader
        title="Shipping & Courier"
        subtitle="Track orders shipped via third-party courier partners"
        actions={
          <button data-testid="new-shipment-button" onClick={() => { setForm(EMPTY); setOpen(true); }} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
            <Plus size={16} /> New Shipment
          </button>
        }
      />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat icon={Navigation} label="In Transit" value={inTransit} accent="text-[#4DA3FF]" />
          <Stat icon={PackageCheck} label="Delivered" value={delivered} accent="text-[#00FF66]" />
          <Stat icon={Clock} label="Pending Pickup" value={pending} accent="text-zinc-400" />
        </div>

        <div className="flex flex-wrap gap-3">
          <select data-testid="ship-filter-status" className={selCls} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            {SHIPMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {shipments.length === 0 ? (
          <EmptyState icon={Truck} title="No shipments" hint="Create a shipment to start tracking deliveries." />
        ) : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-white/10 bg-[#0a0a0a]">
                    {["Brand", "Customer", "Items", "Courier", "Tracking #", "COD", "ETA", "Status", ""].map((h) => (
                      <th key={h} className="py-3 px-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s) => (
                    <tr key={s.id} data-testid={`shipment-row-${s.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                      <td className="py-3 px-3 text-zinc-200">{s.brand}</td>
                      <td className="py-3 px-3 text-zinc-300">{s.customer_name}<div className="text-[10px] text-zinc-600">{s.address}</div></td>
                      <td className="py-3 px-3 text-zinc-400 max-w-[160px] truncate">{s.items}</td>
                      <td className="py-3 px-3 text-zinc-300">{s.courier}</td>
                      <td className="py-3 px-3 font-mono text-xs text-[#4DA3FF]">{s.tracking_number || "—"}</td>
                      <td className="py-3 px-3 font-mono text-zinc-300">{s.cod_amount ? formatINR(s.cod_amount) : "Prepaid"}</td>
                      <td className="py-3 px-3 font-mono text-xs text-zinc-400">{s.status === "Delivered" ? s.delivered_date : (s.expected_delivery || "—")}</td>
                      <td className="py-3 px-3">
                        <select
                          data-testid={`shipment-status-${s.id}`}
                          value={s.status}
                          onChange={(e) => updateStatus(s.id, e.target.value)}
                          className={`text-[10px] font-mono uppercase tracking-wider rounded-sm border px-2 py-1 bg-transparent focus:outline-none cursor-pointer ${shipmentClasses(s.status)}`}
                        >
                          {SHIPMENT_STATUSES.map((st) => <option key={st} value={st} className="bg-[#121212] text-white">{st}</option>)}
                        </select>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`simulate-${s.id}`} title="Simulate courier update" onClick={() => simulate(s.id)} className="text-zinc-500 hover:text-[#4DA3FF] transition-colors p-1"><Zap size={15} /></button>
                          <button data-testid={`return-${s.id}`} title="Return / RTO" onClick={() => openReturn(s)} className="text-zinc-500 hover:text-[#FF9500] transition-colors p-1"><RotateCcw size={15} /></button>
                          <button data-testid={`delete-shipment-${s.id}`} onClick={() => remove(s.id)} className="text-zinc-600 hover:text-[#FF6259] transition-colors p-1"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New Shipment</DialogTitle>
            <DialogDescription className="sr-only">Create a courier shipment for a customer order.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="ship-brand-select" className={inputCls} value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}>
                <option value="">Brand *</option>
                {clients.map((c) => <option key={c.id} value={c.brand}>{c.brand}</option>)}
              </select>
              <input data-testid="ship-customer-input" className={inputCls} placeholder="Customer name *" value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} />
            </div>
            <input data-testid="ship-email-input" type="email" className={inputCls} placeholder="Customer email (for delivery alerts)" value={form.customer_email} onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))} />
            <input className={inputCls} placeholder="Items (e.g. Snake Plant x2)" value={form.items} onChange={(e) => setForm((f) => ({ ...f, items: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <select className={inputCls} value={form.courier} onChange={(e) => setForm((f) => ({ ...f, courier: e.target.value }))}>
                {COURIERS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="ship-tracking-input" className={inputCls} placeholder="Tracking number" value={form.tracking_number} onChange={(e) => setForm((f) => ({ ...f, tracking_number: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select className={inputCls} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                {SHIPMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="number" className={inputCls} placeholder="COD amount (₹, 0 = prepaid)" value={form.cod_amount} onChange={(e) => setForm((f) => ({ ...f, cod_amount: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Ship date</label>
                <input type="date" className={inputCls} value={form.ship_date} onChange={(e) => setForm((f) => ({ ...f, ship_date: e.target.value }))} />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Expected delivery</label>
                <input type="date" className={inputCls} value={form.expected_delivery} onChange={(e) => setForm((f) => ({ ...f, expected_delivery: e.target.value }))} />
              </div>
            </div>
            <input className={inputCls} placeholder="Delivery address / city" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            <button data-testid="ship-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">Create Shipment</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={retOpen} onOpenChange={setRetOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Return / RTO</DialogTitle>
            <DialogDescription className="sr-only">Log a return and optionally refund against an invoice.</DialogDescription>
          </DialogHeader>
          {retShip && (
            <div className="space-y-3 pt-2">
              <div className="text-sm text-zinc-400">{retShip.brand} · {retShip.customer_name} · <span className="font-mono text-[#4DA3FF]">{retShip.tracking_number}</span></div>
              <textarea data-testid="return-reason-input" className={inputCls} rows={3} placeholder="Reason for return / RTO *" value={ret.reason} onChange={(e) => setRet((r) => ({ ...r, reason: e.target.value }))} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="return-refund-input" type="number" className={inputCls} placeholder="Refund amount (₹)" value={ret.refund_amount} onChange={(e) => setRet((r) => ({ ...r, refund_amount: e.target.value }))} />
                <select data-testid="return-invoice-select" className={inputCls} value={ret.invoice_id} onChange={(e) => setRet((r) => ({ ...r, invoice_id: e.target.value }))}>
                  <option value="">Refund against invoice…</option>
                  {invoices.filter((i) => i.client === retShip.brand).map((i) => (
                    <option key={i.id} value={i.id}>{i.service || "Invoice"} · {formatINR(i.received)}/{formatINR(i.amount)}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-zinc-500">Selecting an invoice reduces its received amount by the refund and recomputes the balance.</p>
              <button data-testid="return-submit-button" onClick={submitReturn} className="w-full text-sm bg-[#FF9500]/15 border border-[#FF9500]/40 text-[#FF9500] font-medium py-2.5 rounded-sm hover:bg-[#FF9500]/25 transition-colors">Log Return & Refund</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
